import type { Spell } from '@character-creator/types';

export const level8Spells: Spell[] = [
  {
    id: 'antimagic-field',
    name: 'Antimagic Field',
    level: 8,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Self (10-foot-radius sphere)',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pinch of powdered iron or iron filings' },
    duration: '1 hour',
    concentration: true,
    description: "A 10-foot-radius invisible sphere of antimagic surrounds you. This area is divorced from the magical energy that suffuses the multiverse. Within the sphere, spells can't be cast, summoned creatures disappear, and even magic items become mundane. Until the spell ends, the sphere moves with you, centered on you. Spells and other magical effects, except those created by an artifact or a deity, are suppressed in the sphere and can't protrude into it.",
    classes: ['cleric', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'antipathy-sympathy',
    name: 'Antipathy/Sympathy',
    level: 8,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 hour',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'either a lump of alum soaked in vinegar for the antipathy effect or a drop of honey for the sympathy effect' },
    duration: '10 days',
    concentration: false,
    description: "This spell attracts or repels creatures of your choice. You target something within range, either a Huge or smaller object or creature or an area that is no larger than a 200-foot cube. Then specify a kind of intelligent creature, such as red dragons, goblins, or vampires. You invest the target with an aura that either attracts or repels the specified creatures for the duration. Choose antipathy or sympathy as the aura's effect.",
    classes: ['druid', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'clone',
    name: 'Clone',
    level: 8,
    school: 'necromancy',
    ritual: false,
    castingTime: '1 hour',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a diamond worth at least 1,000 gp and at least 1 cubic inch of flesh of the creature that is to be cloned, which the spell consumes, and a vessel worth at least 2,000 gp that has a sealable lid and is large enough to hold a Medium creature', materialCost: 3000, materialConsumed: true },
    duration: 'Instantaneous',
    concentration: false,
    description: "This spell grows an inert duplicate of a living creature as a safeguard against death. This clone forms inside a sealed vessel and grows to full size and maturity after 120 days; you can also choose to have the clone be a younger version of the same creature. It remains inert and endures indefinitely, as long as its vessel remains undisturbed. At any time after the clone matures, if the original creature dies, its soul transfers to the clone, provided that the soul is free and willing to return.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'control-weather',
    name: 'Control Weather',
    level: 8,
    school: 'transmutation',
    ritual: false,
    castingTime: '10 minutes',
    range: 'Self (5-mile radius)',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'burning incense and bits of earth and wood mixed in water' },
    duration: '8 hours',
    concentration: true,
    description: "You take control of the weather within 5 miles of you for the duration. You must be outdoors to cast this spell. Moving to a place where you don't have a clear path to the sky ends the spell early. When you cast the spell, you change the current weather conditions, which are determined by the DM based on the climate and season. You can change precipitation, temperature, and wind.",
    classes: ['cleric', 'druid', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'demiplane',
    name: 'Demiplane',
    level: 8,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: false, somatic: true, material: false },
    duration: '1 hour',
    concentration: false,
    description: "You create a shadowy door on a flat solid surface that you can see within range. The door is large enough to allow Medium creatures to pass through unhindered. When opened, the door leads to a demiplane that appears to be an empty room 30 feet in each dimension, made of wood or stone. When the spell ends, the door disappears, and any creatures or objects inside the demiplane remain trapped there, as the door also disappears from the other side.",
    classes: ['warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'dominate-monster',
    name: 'Dominate Monster',
    level: 8,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 hour',
    concentration: true,
    description: "You attempt to beguile a creature that you can see within range. It must succeed on a Wisdom saving throw or be charmed by you for the duration. If you or creatures that are friendly to you are fighting it, it has advantage on the saving throw. While the creature is charmed, you have a telepathic link with it as long as the two of you are on the same plane of existence. You can use this telepathic link to issue commands to the creature while you are conscious (no action required), which it does its best to obey.",
    higherLevel: "When you cast this spell with a 9th-level spell slot, the duration is concentration, up to 8 hours.",
    classes: ['bard', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'earthquake',
    name: 'Earthquake',
    level: 8,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '500 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pinch of dirt, a piece of rock, and a lump of clay' },
    duration: '1 minute',
    concentration: true,
    description: "You create a seismic disturbance at a point on the ground that you can see within range. For the duration, an intense tremor rips through the ground in a 100-foot-radius circle centered on that point and shakes creatures and structures in contact with the ground in that area. The ground in the area becomes difficult terrain. Each creature on the ground that is concentrating must make a Constitution saving throw. On a failed save, the creature's concentration is broken.",
    classes: ['cleric', 'druid', 'sorcerer'],
    source: 'srd-5.1',
  },
  {
    id: 'feeblemind',
    name: 'Feeblemind',
    level: 8,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '150 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a handful of clay, crystal, glass, or mineral spheres' },
    duration: 'Instantaneous',
    concentration: false,
    description: "You blast the mind of a creature that you can see within range, attempting to shatter its intellect and personality. The target takes 4d6 psychic damage and must make an Intelligence saving throw. On a failed save, the creature's Intelligence and Charisma scores become 1. The creature can't cast spells, activate magic items, understand language, or communicate in any intelligible way. The creature can, however, identify its friends, follow them, and even protect them. At the end of every 30 days, the creature can repeat its saving throw against this spell. If it succeeds, the spell ends.",
    classes: ['bard', 'druid', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'glibness',
    name: 'Glibness',
    level: 8,
    school: 'transmutation',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: false, material: false },
    duration: '1 hour',
    concentration: false,
    description: "Until the spell ends, when you make a Charisma check, you can replace the number you roll with a 15. Additionally, no matter what you say, magic that would determine if you are telling the truth indicates that you are being truthful.",
    classes: ['bard', 'warlock'],
    source: 'srd-5.1',
  },
  {
    id: 'holy-aura',
    name: 'Holy Aura',
    level: 8,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Self',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a tiny reliquary worth at least 1,000 gp containing a sacred relic, such as a scrap of cloth from a saint\'s robe or a piece of parchment from a religious text', materialCost: 1000 },
    duration: '1 minute',
    concentration: true,
    description: "Divine light washes out from you and coalesces in a soft radiance in a 30-foot radius around you. Creatures of your choice in that radius when you cast this spell shed dim light in a 5-foot radius and have advantage on all saving throws, and other creatures have disadvantage on attack rolls against them until the spell ends. In addition, when a fiend or an undead hits an affected creature with a melee attack, the aura flashes with brilliant light. The attacker must succeed on a Constitution saving throw or be blinded until the spell ends.",
    classes: ['cleric'],
    source: 'srd-5.1',
  },
  {
    id: 'incendiary-cloud',
    name: 'Incendiary Cloud',
    level: 8,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '150 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '1 minute',
    concentration: true,
    description: "A swirling cloud of smoke shot through with white-hot embers appears in a 20-foot-radius sphere centered on a point within range. The cloud spreads around corners and is heavily obscured. It lasts for the duration or until a wind of moderate or greater speed (at least 10 miles per hour) disperses it. When the cloud appears, each creature in it must make a Dexterity saving throw. A creature takes 10d8 fire damage on a failed save, or half as much damage on a successful one. A creature must also make this saving throw when it enters the spell's area for the first time on a turn or ends its turn there.",
    classes: ['sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'maze',
    name: 'Maze',
    level: 8,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: true, material: false },
    duration: '10 minutes',
    concentration: true,
    description: "You banish a creature that you can see within range into a labyrinthine demiplane. The target remains there for the duration or until it escapes the maze. The target can use its action to attempt to escape. When it does so, it makes a DC 20 Intelligence check. If it succeeds, it escapes, and the spell ends (a minotaur or goristro demon automatically succeeds). When the spell ends, the target reappears in the space it left or, if that space is occupied, in the nearest unoccupied space.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'mind-blank',
    name: 'Mind Blank',
    level: 8,
    school: 'abjuration',
    ritual: false,
    castingTime: '1 action',
    range: 'Touch',
    components: { verbal: true, somatic: true, material: false },
    duration: '24 hours',
    concentration: false,
    description: "Until the spell ends, one willing creature you touch is immune to psychic damage, any effect that would sense its emotions or read its thoughts, divination spells, and the charmed condition. The spell even foils wish spells and spells or effects of similar power used to affect the target's mind or to gain information about the target.",
    classes: ['bard', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'power-word-stun',
    name: 'Power Word Stun',
    level: 8,
    school: 'enchantment',
    ritual: false,
    castingTime: '1 action',
    range: '60 feet',
    components: { verbal: true, somatic: false, material: false },
    duration: 'Instantaneous',
    concentration: false,
    description: "You speak a word of power that can overwhelm the mind of one creature you can see within range, leaving it dumbfounded. If the target has 150 hit points or fewer, it is stunned. Otherwise, the spell has no effect. The stunned target must make a Constitution saving throw at the end of each of its turns. On a successful save, this stunning effect ends.",
    classes: ['bard', 'sorcerer', 'warlock', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'sunburst',
    name: 'Sunburst',
    level: 8,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: '150 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'fire and a piece of sunstone' },
    duration: 'Instantaneous',
    concentration: false,
    description: "Brilliant sunlight flashes in a 60-foot radius centered on a point you choose within range. Each creature in that light must make a Constitution saving throw. On a failed save, a creature takes 12d6 radiant damage and is blinded for 1 minute. On a successful save, it takes half as much damage and isn't blinded by this spell. Undead and oozes have disadvantage on this saving throw. A creature blinded by this spell makes another Constitution saving throw at the end of each of its turns. On a successful save, it is no longer blinded.",
    classes: ['druid', 'sorcerer', 'wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'telepathy',
    name: 'Telepathy',
    level: 8,
    school: 'evocation',
    ritual: false,
    castingTime: '1 action',
    range: 'Unlimited',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a pair of linked silver rings' },
    duration: '24 hours',
    concentration: false,
    description: "You create a telepathic link between yourself and a willing creature with which you are familiar. The creature can be anywhere on the same plane of existence as you. The spell ends if you or the target are no longer on the same plane. Until the spell ends, you and the target can instantaneously share words, images, sounds, and other sensory messages with one another through the link, and the target recognizes you as the creature it is communicating with.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
  {
    id: 'trap-the-soul',
    name: 'Trap the Soul',
    level: 8,
    school: 'conjuration',
    ritual: false,
    castingTime: '1 action or 1 minute',
    range: '30 feet',
    components: { verbal: true, somatic: true, material: true, materialDescription: 'a small silver cage worth 100 gp or a tiny jewel worth 1,000 gp', materialCost: 1000 },
    duration: 'Until dispelled',
    concentration: false,
    description: "You can trap a creature's soul in a gem or a specially prepared container. A creature can be trapped in one of two ways: by naming it specifically or by luring it into a prepared gem. If the target is present when you cast this spell, it must make a Charisma saving throw. If it fails, its soul is transferred into the gem or container.",
    classes: ['wizard'],
    source: 'srd-5.1',
  },
];
